import React from 'react';

import { RadioGroupInput as OldRadioGroupInput } from '@components/Form';
import { TextInput as OldTextInput, TextInputProps } from '@components/Forms/TextInput';
import {
  GoodsDisplay as OldGoodsDisplay,
  MembershipNameTextDisplay as OldMembershipNameTextDisplay,
  MembershipTextDisplay as OldMembershipTextDisplay,
  YesNoDisplay as OldYesNoDisplay,
} from '@components/utilities/MembershipForm/Form';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import OldRadioGroup from '@material-ui/core/RadioGroup';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { Field } from '@utils/forms';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  leftColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    flex: 2.5,
  },
  rightColumn: {
    ...common.flexColumn,
    flex: 1,
  },
  textDisplay: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    padding: '0.5rem 0.5rem',
  },
  checklistTextDisplay: {
    color: theme.palette.grey['700'],
    padding: '0.5rem 0.5rem',
  },
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  radioGroupLabel: {
    ...common.middleTextImportant,
    paddingTop: 2,
    color: theme.palette.text.secondary,
  },
  formControlRoot: {
    alignSelf: 'center',
  },
  formControlTitle: {
    ...common.middleText,
    color: theme.palette.text.secondary,
    marginBottom: 5,
  },
  radioGroupRadio: {
    padding: 5,
  },
  label: {
    ...common.middleText,
    minWidth: 240,
    textAlign: 'left',
    padding: '12px 0.5rem',
    color: theme.palette.text.secondary,
  },
  input: {
    ...common.middleText,
    color: theme.palette.text.secondary,
  },
}));

export const MembershipTextDisplay: typeof OldMembershipTextDisplay = (props) => {
  const classes = useStyles();

  return <OldMembershipTextDisplay {...props} classes={{ container: classes.textDisplay }} />;
};

export const MembershipNameTextDisplay: typeof OldMembershipNameTextDisplay = (props) => {
  const classes = useStyles();

  return <OldMembershipNameTextDisplay {...props} classes={{ container: classes.textDisplay }} />;
};

export const YesNoDisplay: typeof OldYesNoDisplay = (props) => {
  const classes = useStyles();

  return <OldYesNoDisplay {...props} classes={{ container: classes.checklistTextDisplay }} />;
};

export const GoodsDisplay: typeof OldGoodsDisplay = (props) => {
  const classes = useStyles();

  return <OldGoodsDisplay {...props} classes={{ container: classes.checklistTextDisplay }} />;
};

export interface RadioGroupProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  options: {
    value: string;
    label: string;
  }[];
}

export const RadioGroup: React.FC<RadioGroupProps> = (props) => {
  const { title, value, onChange, options } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <FormControl className={classes.formControlRoot} component="fieldset">
      <div className={classes.formControlTitle}>{title}</div>
      <OldRadioGroup value={value} onChange={(_, v) => onChange(v)}>
        {options.map(({ value: v, label }) => (
          <FormControlLabel
            key={v}
            value={v}
            control={<Radio className={classes.radioGroupRadio} />}
            label={label}
            classes={{
              label: classes.radioGroupLabel,
            }}
          />
        ))}
      </OldRadioGroup>
    </FormControl>
  );
};

export const TextInput: typeof OldTextInput = (props) => {
  const { formClasses, classes: textClasses, ...rest } = props;

  const classes = useStyles();

  return (
    <OldTextInput
      formClasses={{
        label: classes.label,
        ...formClasses,
      }}
      classes={{
        input: classes.input,
        ...textClasses,
      }}
      {...rest}
    />
  );
};

export interface MembershipTextInputProps extends Partial<TextInputProps> {
  field: Field<any>;
}

export const MembershipTextInput: React.FC<MembershipTextInputProps> = (props) => {
  const { field, required, ...rest } = props;
  const classes = useCommonStyles();

  const renderRequiredLabel = () => <span className={classes.textErrorImportant}>【必須】</span>;

  return (
    <TextInput
      label={
        <>
          {field.label}
          {required && renderRequiredLabel()}
        </>
      }
      name={field.name}
      multiline={field.options.multiline}
      {...rest}
    />
  );
};

export const RadioGroupInput: typeof OldRadioGroupInput = (props) => {
  const { formClasses, ...rest } = props;

  const classes = useStyles();

  return (
    <OldRadioGroupInput
      formClasses={{
        label: classes.label,
        ...formClasses,
      }}
      formControlLabelClasses={{
        label: classes.input,
      }}
      {...rest}
    />
  );
};
